import { GoogleMap, Marker } from '@react-google-maps/api'
import { forwardRef, useCallback, useContext, useImperativeHandle, useMemo, useRef } from 'react'

import { GoogleMapsContext } from 'src/features/google-maps/google-maps-context'
import MapMarkerIcon from 'src/shared/assets/images/map-marker-icon.png'

interface BusinessMapProps {
  draggable: boolean
  zoomControl?: boolean
  borderRadius?: string
  zoom?: number
  markerIconSize?: number
  markers?: {
    lat: number
    lng: number
    name: string
    size?: number
  }[]
}

const BusinessMap = forwardRef<google.maps.Map, BusinessMapProps>(
  ({ draggable, zoomControl = true, borderRadius = '40px', zoom = 14, markerIconSize = 50, markers = [] }, ref) => {
    const mapRef = useRef<google.maps.Map>(null)
    const { isLoaded } = useContext(GoogleMapsContext)

    const mapLoaded = useCallback((map: google.maps.Map) => {
      mapRef.current = map
    }, [])

    useImperativeHandle(ref, () => mapRef.current as google.maps.Map, [])

    const markerIconWidth = markerIconSize
    const markerIconHeight = (markerIconSize * 44) / 40

    const center = useMemo(() => {
      if (markers.length > 0) {
        const lastMarker = markers[markers.length - 1]
        return {
          lat: lastMarker.lat,
          lng: lastMarker.lng,
        }
      }
      return {
        lat: 40,
        lng: -70,
      }
    }, [markers])

    const renderMarkers = useCallback(() => {
      return markers.map((marker) => (
        <Marker
          icon={{
            url: MapMarkerIcon,
            scaledSize: new google.maps.Size(markerIconWidth, markerIconHeight),
            fillOpacity: 0.2,
          }}
          position={{
            lat: marker.lat,
            lng: marker.lng,
          }}
          options={{
            clickable: false,
          }}
          key={marker.name}
        />
      ))
    }, [markers])

    if (!isLoaded) {
      return <div className="size-full"></div>
    }

    return (
      <GoogleMap
        onLoad={mapLoaded}
        mapContainerStyle={{
          width: '100%',
          height: '100%',
          borderRadius: borderRadius,
        }}
        zoom={zoom}
        options={{
          disableDefaultUI: true,
          zoomControl: zoomControl,
          draggable: draggable,
          clickableIcons: false,
          mapId: '6df6d8c2491ef38d15ac8174',
          center: {
            lat: center.lat,
            lng: center.lng,
          },
        }}
      >
        {renderMarkers()}
      </GoogleMap>
    )
  },
)

BusinessMap.displayName = 'BusinessMap'

export default BusinessMap
